import React from "react";
import { AlertTriangle, X } from "lucide-react";
import type { OrderTodo, TodoStatus, OrderTodoDependency } from "../../../../domain/types/todo";
import { TODO_STATUS_LABELS } from "../../../../domain/types/todo";

interface DependencyWarningDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  todo: OrderTodo | null;
  targetStatus: TodoStatus | null;
  warningDependencies: OrderTodoDependency[];
  isLoading?: boolean;
}

export const DependencyWarningDialog: React.FC<DependencyWarningDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  todo,
  targetStatus,
  warningDependencies,
  isLoading = false,
}) => {
  if (!isOpen || !todo || !targetStatus) return null;

  const incompleteWarnings = warningDependencies.filter(
    (d) => d.dependsOnTodoStatus !== "COMPLETED"
  );

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Dialog */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-start gap-3 p-4 border-b">
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="text-yellow-600" size={20} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">
                선행 작업 미완료 경고
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                이 작업의 선행 작업이 아직 완료되지 않았습니다.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="p-4">
            <p className="text-sm text-gray-600 mb-4">
              <strong>"{todo.name}"</strong>의 상태를{" "}
              <strong>"{TODO_STATUS_LABELS[targetStatus]}"</strong>(으)로 변경하시겠습니까?
            </p>

            {incompleteWarnings.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <p className="text-sm font-medium text-yellow-800 mb-2">
                  미완료된 선행 작업:
                </p>
                <ul className="space-y-1">
                  {incompleteWarnings.map((dep) => (
                    <li
                      key={dep.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-yellow-700">{dep.dependsOnTodoName}</span>
                      <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                        {TODO_STATUS_LABELS[dep.dependsOnTodoStatus]}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-sm text-gray-500">
              계속 진행하면 선행 작업 담당자에게 알림이 전송됩니다.
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={isLoading}
            >
              취소
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:bg-yellow-400 disabled:cursor-not-allowed"
            >
              {isLoading ? "처리 중..." : "계속 진행"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DependencyWarningDialog;
